/**
 * 模擬試験 (60 分で 2 問)。始める前の画面、試験中、結果の 3 つの形を持つ。
 * 決まりと記録は lib/coding-test-mock.ts (localStorage だけ。サーバーには送らない)。
 * 試験中の 1 問は StdioTryPanel をそのまま使う (mock の差分で、解答の札と練習の記録を外し、頭に時計、下に移動の帯を置く)。
 * 問題の本文は始めてから取りに行く (組は始めるときに決まるので、頁の HTML には入れない)
 */
import { type ReactNode, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link } from "react-router";
import { Button } from "@noiman/ui/button";
import { PageFooter } from "@noiman/ui/page-footer";
import { PublicPageBand } from "@/components/page-band/PublicPageBand";
import { PublicPageShell } from "@/components/common/PublicPageShell";
import { StdioTryPanel } from "@/components/coding-test/StdioTryPanel";
import { ExamplesBlock, ProblemSection } from "@/components/coding-test/ProblemBody";
import { CODING_INDEX_PATH, phraseText } from "@/lib/coding-interview";
import { CODING_TEST_INDEX_PATH, type CodingTestTier, codingTestPath, codingTestTierLabel } from "@/lib/coding-test";
import {
  MOCK_MINUTES,
  MOCK_URGENT_MS,
  MOCK_WARN_MS,
  type MockEnd,
  type MockProblemRef,
  type MockRun,
  type MockSet,
  type MockState,
  RANK_GUIDE,
  buildMockSets,
  clearMockRun,
  finishMockRun,
  formatClock,
  formatDuration,
  isRunning,
  readMockState,
  recordMockSubmission,
  remainingMs,
  settleMockRun,
  startMockRun,
  summarize,
} from "@/lib/coding-test-mock";
import { writeDraft } from "@/lib/coding-test-progress";
import type { MockProblem } from "@/lib/coding-test-mock-problems.client";

export type MockProblemInfo = MockProblemRef & { title: string };

const WRAP = "[word-break:keep-all] [overflow-wrap:break-word]";
const WIDE = "(min-width: 768px)";
const CRUMBS = [
  { label: "技術面接問題", to: "/interview-questions" },
  { label: "コーディング面接問題", to: CODING_INDEX_PATH },
  { label: "コーディングテスト", to: CODING_TEST_INDEX_PATH },
];

function subscribeWide(onChange: () => void) {
  const query = window.matchMedia(WIDE);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/* サーバーと最初の描画は PC として描く。スマホの注意は描いたあとに出る */
function useWide() {
  return useSyncExternalStore(
    subscribeWide,
    () => window.matchMedia(WIDE).matches,
    () => true,
  );
}

export function MockExam({ problems }: { problems: MockProblemInfo[] }) {
  const sets = useMemo(() => buildMockSets(problems), [problems]);
  const info = useMemo(() => new Map(problems.map((p) => [p.slug, p])), [problems]);
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<MockState>({ run: null, history: [] });
  const [now, setNow] = useState(0);
  const [storageFailed, setStorageFailed] = useState(false);
  const wide = useWide();

  /* 保存した物は描いたあとに読む。時間が過ぎていたら、ここで時間切れとして終える */
  useEffect(() => {
    const t = Date.now();
    settleMockRun(t);
    setState(readMockState());
    setNow(t);
    setReady(true);
  }, []);

  const run = state.run;
  const running = ready && run !== null && run.finishedAt === null;
  useEffect(() => {
    if (!running || !run) return;
    const timer = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (remainingMs(run, t) === 0) {
        settleMockRun(t);
        setState(readMockState());
      }
    }, 500);
    return () => clearInterval(timer);
  }, [running, run]);

  const refresh = () => setState(readMockState());

  const start = (set: MockSet) => {
    /* 前の書きかけを持ち込まない。始めるたびに、書き始めのコードから */
    for (const slug of set.slugs) for (const language of ["python", "javascript"] as const) writeDraft(`mock-${slug}`, language, null);
    const t = Date.now();
    startMockRun(set, t);
    setNow(t);
    const saved = readMockState();
    setState(saved);
    /* 時計は保存した始めの時刻で続けるので、保存できない設定 (プライベートの窓など) では始められない */
    setStorageFailed(saved.run === null);
  };
  const finish = (endedBy: MockEnd) => {
    finishMockRun(Date.now(), endedBy);
    refresh();
  };
  const submitted = (slug: string, result: { passed: number; total: number; language: "python" | "javascript"; at: number }) => {
    recordMockSubmission(slug, result);
    refresh();
  };
  const again = () => {
    clearMockRun();
    refresh();
  };

  if (ready && run && run.finishedAt === null && isRunning(run, now)) {
    if (!wide) return <NarrowRunning onFinish={() => finish("quit")} />;
    return <ExamView run={run} now={now} onSubmitted={submitted} onFinish={finish} />;
  }
  if (ready && run && run.finishedAt !== null) return <ResultView run={run} info={info} onAgain={again} />;
  return <Intro ready={ready} wide={wide} sets={sets} info={info} state={state} storageFailed={storageFailed} onStart={start} />;
}

/* ---- 始める前 ---- */

const RULES = [
  "制限時間は 60 分です。やさしい問題 1 問とふつうの問題 1 問を、好きな順に解けます",
  "1 問ずつ「提出して採点」で、隠れたテストを全部走らせます。全部通った問題が「通過」です。何回でも出し直せます",
  "試験中は、ヒントも解答も見られません。書いたコードは、この端末のブラウザの中だけに残ります",
  "再読み込みしても、時計は続きます。時間が来たら、それまでの提出で数えて、結果に進みます",
  "結果も、この端末のブラウザの中だけに残ります。サーバーには送りません",
];

function Intro({
  ready,
  wide,
  sets,
  info,
  state,
  storageFailed,
  onStart,
}: {
  ready: boolean;
  wide: boolean;
  sets: MockSet[];
  info: Map<string, MockProblemInfo>;
  state: MockState;
  storageFailed: boolean;
  onStart: (set: MockSet) => void;
}) {
  const triedAt = (id: string) => state.history.find((h) => h.setId === id);
  const firstUntried = sets.find((s) => !triedAt(s.id)) ?? sets[0];
  const [chosen, setChosen] = useState<string | null>(null);
  const current = sets.find((s) => s.id === (chosen ?? firstUntried?.id)) ?? null;
  const [warm, setWarm] = useState<"idle" | "loading" | "done" | "failed">("idle");

  const warmUp = async () => {
    setWarm("loading");
    try {
      const { createStdioJudge } = await import("@/lib/stdio-judge");
      const judge = createStdioJudge();
      const report = await judge.run({ language: "python", code: "print(1)", stdin: "" });
      judge.dispose();
      setWarm(report.status === "ran" ? "done" : "failed");
    } catch {
      setWarm("failed");
    }
  };

  const minutes = current ? current.slugs.reduce((sum, slug) => sum + (info.get(slug)?.estimatedMinutes ?? 0), 0) : 0;
  const last = state.history[0];

  return (
    <PublicPageShell>
      <PublicPageBand title="コーディングテストの模擬試験" column={{ max: 1080, pad: 40 }} crumbs={CRUMBS} />
      <div className="lab-brand mx-auto w-full max-w-[1080px] px-4 py-6 sm:px-5 sm:py-8 md:px-10">
        <div className="flex max-w-[860px] flex-col gap-8" data-mock-intro>
          <p className={`m-0 text-[17px] leading-[1.8] text-main ${WRAP}`}>
            {phraseText("本番の試験と同じ 60 分で 2 問を解く練習です。時間を計り、1 問ずつ提出して、全部のテストに通ったかで数えます。")}
          </p>

          <section>
            <h2 className="m-0 mb-3 text-[22px] font-bold text-main">決まり</h2>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {RULES.map((rule) => (
                <li key={rule} className={`rounded-lg border border-main bg-surface px-4 py-3 text-[17px] leading-[1.8] text-main ${WRAP}`}>
                  {phraseText(rule)}
                </li>
              ))}
            </ul>
          </section>

          {!wide ? (
            <p
              data-mock-pc-only
              className={`m-0 rounded-lg border border-main bg-second p-4 text-[17px] font-bold leading-[1.8] text-main ${WRAP}`}
            >
              {phraseText("模擬試験は PC で開いてください。本番の試験も PC で受けることが多く、スマホでは 60 分の通しで解くのが難しいためです。1 問ずつの練習は、スマホでも使えます。")}
            </p>
          ) : null}

          {sets.length === 0 ? (
            <p className="m-0 text-[17px] leading-[1.8] text-main">模擬試験に出せる問題の組が、まだありません。</p>
          ) : (
            <section>
              <h2 className="m-0 mb-3 text-[22px] font-bold text-main">組を選ぶ</h2>
              <p className={`m-0 mb-3 text-[17px] leading-[1.8] text-sub ${WRAP}`}>
                {phraseText("どの組も、やさしい問題 1 問とふつうの問題 1 問です。問題の題は、始めるまで見えません。")}
              </p>
              <div role="radiogroup" aria-label="組を選ぶ" className="flex flex-col gap-2" data-mock-sets>
                {sets.map((set, index) => {
                  const tried = triedAt(set.id);
                  const selected = current?.id === set.id;
                  return (
                    <button
                      key={set.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setChosen(set.id)}
                      className={`flex min-h-[56px] flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border-2 px-4 py-3 text-left text-[17px] font-bold ${selected ? "border-primary bg-surface" : "border-main bg-main"} text-main`}
                    >
                      <span>組 {index + 1}</span>
                      <span className="text-[17px] font-normal text-sub">
                        {tried ? `前回 ${tried.solvedCount} / ${tried.problems.length} 問を通過` : "まだ解いていません"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="flex flex-col items-start gap-3">
            <h2 className="m-0 text-[22px] font-bold text-main">始める</h2>
            <p className={`m-0 text-[17px] leading-[1.8] text-main ${WRAP}`}>
              {phraseText("Python の準備 (初めは約 6MB) は、時計が回る前に済ませておけます。始めたあとの最初の実行が、数秒待たされずに済みます。")}
            </p>
            <Button variant="outline" size="lg" onClick={warmUp} disabled={warm === "loading" || warm === "done"} data-mock-warm>
              {warm === "loading" ? "準備しています" : warm === "done" ? "Python の準備ができました" : "Python の準備を先に済ませる"}
            </Button>
            {storageFailed ? (
              <p className={`m-0 rounded-lg border border-main bg-second p-4 text-[17px] font-bold leading-[1.8] text-main ${WRAP}`} data-mock-storage-failed>
                {phraseText("このブラウザでは保存が使えないため、時計を続けられず、始められません。プライベートの窓や、保存を止める設定を外して、もう一度試してください。")}
              </p>
            ) : null}
            {warm === "failed" ? <p className="m-0 text-[17px] text-main">準備を済ませられませんでした。始めたあとの最初の実行で、もう一度読み込みます。</p> : null}
            {current && minutes > 0 ? (
              <p className="m-0 text-[17px] leading-[1.8] text-sub">目安の合計は {minutes} 分です。制限は {MOCK_MINUTES} 分です。</p>
            ) : null}
            <Button variant="primary" size="lg" disabled={!ready || !wide || !current} onClick={() => current && onStart(current)} data-mock-start>
              {MOCK_MINUTES} 分の模擬を始める
            </Button>
          </section>

          {last ? (
            <p className={`m-0 text-[17px] leading-[1.8] text-sub ${WRAP}`} data-mock-last>
              {`前回は ${last.solvedCount} / ${last.problems.length} 問を通過、${formatDuration(last.elapsedMs)}でした。`}
            </p>
          ) : null}

          <Link to={CODING_TEST_INDEX_PATH} className="flex min-h-[44px] items-center text-[17px] font-bold text-primary no-underline">
            コーディングテストの説明へ
          </Link>
        </div>
      </div>
    </PublicPageShell>
  );
}

/* ---- 試験中 ---- */

function NarrowRunning({ onFinish }: { onFinish: () => void }) {
  return (
    <PublicPageShell>
      <div className="mx-auto flex w-full max-w-[860px] flex-col items-start gap-4 px-4 py-8">
        <p className={`m-0 rounded-lg border border-main bg-second p-4 text-[17px] font-bold leading-[1.8] text-main ${WRAP}`} data-mock-pc-only>
          {phraseText("進めている模擬試験があります。PC で開くと続きから解けます。時計は止まりません。")}
        </p>
        <Button variant="outline" size="lg" onClick={onFinish}>
          この模擬試験を終える
        </Button>
      </div>
    </PublicPageShell>
  );
}

function clockClass(remaining: number) {
  if (remaining <= MOCK_URGENT_MS) return "text-red-600";
  if (remaining <= MOCK_WARN_MS) return "text-amber-600";
  return "text-main";
}

function ExamView({
  run,
  now,
  onSubmitted,
  onFinish,
}: {
  run: MockRun;
  now: number;
  onSubmitted: (slug: string, result: { passed: number; total: number; language: "python" | "javascript"; at: number }) => void;
  onFinish: (endedBy: MockEnd) => void;
}) {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState<Record<string, MockProblem> | "failed" | null>(null);
  const [confirm, setConfirm] = useState(false);
  /* 始める前の画面を下まで送っていても、試験は頁の頭から始める */
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);
  const remaining = remainingMs(run, now);
  const summary = summarize(run, now, "finished");

  useEffect(() => {
    let alive = true;
    import("@/lib/coding-test-mock-problems.client")
      .then(({ loadMockProblem }) => Promise.all(run.slugs.map((slug) => loadMockProblem(slug))))
      .then((list) => alive && setLoaded(Object.fromEntries(list.map((item) => [item.problem.slug, item]))))
      .catch(() => alive && setLoaded("failed"));
    return () => {
      alive = false;
    };
    // 組は始めたときに決まり、試験中は変わらない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.setId, run.startedAt]);

  const slug = run.slugs[index];
  const current = loaded && loaded !== "failed" ? loaded[slug] : null;

  /* 頭の帯 (CodingLessonHeader) の右側に入れる。問 1、問 2 の札と、残り時間。帯の高さに収めるため、札は 40px、時計は 26px */
  const bar = (
    <div data-mock-bar className="mr-4 flex items-center gap-4">
      <div role="tablist" aria-label="問題" className="flex gap-2">
        {run.slugs.map((s, i) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={i === index}
            onClick={() => setIndex(i)}
            className={`flex min-h-[40px] items-center gap-2 whitespace-nowrap rounded-lg border-2 px-3 text-[17px] font-bold ${i === index ? "border-primary bg-main text-main" : "border-main bg-surface text-sub"}`}
          >
            問 {i + 1}
            {summary.problems[i].solved ? <span className="text-primary">通過</span> : null}
          </button>
        ))}
      </div>
      <div className="flex items-baseline gap-2 whitespace-nowrap" role="timer" aria-label="残り時間">
        <span className="text-[17px] font-bold text-sub">残り</span>
        <span data-mock-clock className={`text-[26px] font-bold leading-none tabular-nums ${clockClass(remaining)}`}>
          {formatClock(remaining)}
        </span>
      </div>
    </div>
  );

  const footer = (
    <div data-mock-footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-main bg-surface px-4 py-3">
      {confirm ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[17px] font-bold text-main">終えて、結果を見ますか</span>
          <Button variant="primary" size="lg" onClick={() => onFinish("finished")}>
            終える
          </Button>
          <Button variant="outline" size="lg" onClick={() => setConfirm(false)}>
            続ける
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="lg" onClick={() => setConfirm(true)}>
          試験を終えて結果を見る
        </Button>
      )}
      <Button variant="secondary" size="lg" onClick={() => setIndex(index === 0 ? 1 : 0)}>
        {index === 0 ? "問 2 へ" : "問 1 へ"}
      </Button>
    </div>
  );

  const body = current ? (
    <div data-practice-body data-plain-h2 className="flex flex-col">
      <div className="mb-6 flex flex-col items-start">
        <h1 className={`text-[24px] font-bold leading-[1.45] text-main md:text-[28px] ${WRAP}`}>{phraseText(current.problem.title)}</h1>
        <p className="m-0 mt-2 text-[17px] font-bold text-sub">
          {`問 ${index + 1} ・ ${codingTestTierLabel(current.problem.tier)} ・ 目安 ${current.problem.estimatedMinutes} 分 ・ 1 テスト ${current.problem.timeLimitMs / 1000} 秒まで`}
        </p>
      </div>
      <ProblemSection section={current.problem.sections.find((s) => s.id === "problem")!} />
      <ExamplesBlock examples={current.examples} />
    </div>
  ) : null;

  return (
    <div className="flex min-h-screen flex-col bg-main text-main" data-mock-exam>
      <main className="flex-1">
        {loaded === "failed" ? (
          <p className="m-0 p-6 text-[17px] text-main">問題を読み込めませんでした。再読み込みしてください。時計は続いています。</p>
        ) : !current ? (
          <div className="flex flex-col">
            <div className="flex justify-end border-b border-main bg-surface px-4 py-2">{bar}</div>
            <p className="m-0 p-6 text-[17px] text-main">問題を読み込んでいます</p>
          </div>
        ) : (
          <StdioTryPanel
            key={slug}
            slug={`mock-${slug}`}
            timeLimitMs={current.problem.timeLimitMs}
            compare={current.problem.compare}
            examples={current.examples}
            starters={current.starters}
            loadTests={async () => {
              const { loadHiddenTests } = await import("@/lib/coding-test-hidden.client");
              return loadHiddenTests(current.problem);
            }}
            crumbs={[...CRUMBS.map((c) => ({ label: c.label, href: c.to })), { label: "模擬試験" }]}
            menu={{
              title: "模擬試験の決まり",
              content: (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {RULES.map((rule) => (
                    <li key={rule} className={`text-[15px] leading-[1.7] text-main ${WRAP}`}>
                      {rule}
                    </li>
                  ))}
                </ul>
              ),
            }}
            task={<div data-practice-body data-plain-h2 className="flex flex-col">{body}</div>}
            tabs={{ problem: body, answer: <></> }}
            nav={{ indexHref: CODING_TEST_INDEX_PATH, prev: null, next: null }}
            mock={{
              headerAction: bar,
              footer,
              locked: remaining === 0,
              onSubmitted: (result) => onSubmitted(slug, result),
            }}
          />
        )}
      </main>
      <PageFooter LinkComponent={Link} />
    </div>
  );
}

/* ---- 結果 ---- */

function tierLine(tier: CodingTestTier) {
  return codingTestTierLabel(tier);
}

function ResultView({ run, info, onAgain }: { run: MockRun; info: Map<string, MockProblemInfo>; onAgain: () => void }) {
  const finishedAt = run.finishedAt ?? run.startedAt;
  const summary = summarize(run, finishedAt, run.endedBy ?? "finished");
  const target = run.slugs.reduce((sum, slug) => sum + (info.get(slug)?.estimatedMinutes ?? 0), 0);
  const headline =
    summary.solvedCount === summary.problems.length
      ? `${summary.problems.length} 問とも、全部のテストに通りました`
      : summary.solvedCount === 0
        ? "全部のテストに通った問題は、ありませんでした"
        : `${summary.problems.length} 問のうち ${summary.solvedCount} 問が、全部のテストに通りました`;
  const ending = run.endedBy === "time-up" ? "時間切れで終わりました" : run.endedBy === "quit" ? "途中で終えました" : "自分で終えました";

  return (
    <PublicPageShell>
      <PublicPageBand title="模擬試験の結果" column={{ max: 1080, pad: 40 }} crumbs={[...CRUMBS, { label: "模擬試験", to: `${CODING_TEST_INDEX_PATH}/mock` }]} />
      <div className="lab-brand mx-auto w-full max-w-[1080px] px-4 py-6 sm:px-5 sm:py-8 md:px-10">
        <div className="flex max-w-[860px] flex-col gap-8" data-mock-result>
          <section>
            <h2 className={`m-0 text-[26px] font-bold leading-[1.5] text-main ${WRAP}`} data-mock-headline>
              {phraseText(headline)}
            </h2>
            <p className={`m-0 mt-2 text-[17px] leading-[1.8] text-main ${WRAP}`}>
              {phraseText(`${ending}。使った時間は ${formatDuration(summary.elapsedMs)} (制限 ${MOCK_MINUTES} 分) です。この組の目安は 2 問で ${target} 分です。`)}
            </p>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="m-0 text-[22px] font-bold text-main">問題ごと</h2>
            {summary.problems.map((result, i) => {
              const item = info.get(result.slug);
              return (
                <div key={result.slug} className="flex flex-col gap-2 rounded-xl border border-main bg-surface p-4" data-mock-problem>
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <p className={`m-0 text-[19px] font-bold text-main ${WRAP}`}>{`問 ${i + 1}  ${item?.title ?? result.slug}`}</p>
                    <span
                      className={`rounded-full border px-3 py-0.5 text-[17px] font-bold ${result.solved ? "border-primary text-primary" : "border-main text-sub"}`}
                    >
                      {result.solved ? "通過" : "未通過"}
                    </span>
                  </div>
                  <p className={`m-0 text-[17px] leading-[1.8] text-main ${WRAP}`}>
                    {phraseText(
                      [
                        item ? `${tierLine(item.tier)}、目安 ${item.estimatedMinutes} 分` : "",
                        result.solved && result.solvedAfterMs !== null ? `全部通るまで ${formatDuration(result.solvedAfterMs)}` : "",
                        result.submissions === 0
                          ? "提出はありませんでした"
                          : `提出 ${result.submissions} 回${result.last && !result.solved ? `、最後は ${result.last.passed} / ${result.last.total} 件が通過` : ""}`,
                      ]
                        .filter(Boolean)
                        .join("。") + "。",
                    )}
                  </p>
                  <Link to={codingTestPath(result.slug)} className="flex min-h-[44px] items-center text-[17px] font-bold text-primary no-underline">
                    この問題を、解答つきの練習で解き直す
                  </Link>
                </div>
              );
            })}
          </section>

          <section className="flex flex-col gap-3" data-mock-rank>
            <h2 className="m-0 text-[22px] font-bold text-main">ランクの目安と見比べる</h2>
            <p className={`m-0 text-[17px] leading-[1.8] text-main ${WRAP}`}>
              {phraseText(
                "選考で使われるスキルチェックの、公開されているランクの目安です。この模擬のやさしい問題は C ランク、ふつうの問題は B ランクの水準を想定して、目安の時間を決めています。",
              )}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] border-collapse text-[17px] text-main">
                <thead>
                  <tr>
                    {["ランク", "上位の目安", "解答時間の中央値"].map((head) => (
                      <th key={head} className="border-b-2 border-main px-3 py-2 text-left font-bold">
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {RANK_GUIDE.map((row) => (
                    <tr key={row.rank}>
                      <td className="border-b border-main px-3 py-2 font-bold">{row.rank}</td>
                      <td className="border-b border-main px-3 py-2">{row.top}</td>
                      <td className="border-b border-main px-3 py-2">{row.median}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={`m-0 text-[17px] leading-[1.8] text-sub ${WRAP}`}>
              {phraseText(
                "出典は paiza のスキルチェックで公開されている数です。この頁の結果はランクの判定ではなく、時間と全部のテストに通ったかを、目安と見比べるためのものです。",
              )}
            </p>
          </section>

          <div className="flex flex-wrap gap-3">
            <Button variant="primary" size="lg" onClick={onAgain} data-mock-again>
              別の組で、もう一度やる
            </Button>
            <Link to={CODING_TEST_INDEX_PATH} className="flex min-h-[44px] items-center text-[17px] font-bold text-primary no-underline">
              コーディングテストの説明へ
            </Link>
          </div>
        </div>
      </div>
    </PublicPageShell>
  );
}
